import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Paper,
  IconButton,
  Divider,
  TextField,
  Stack,
  Card,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useAccessibility } from '../contexts/AccessibilityContext';

const Cart = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, clearCart, totalPrice } = useCart();
  const { isAuthenticated } = useAuth();
  const { announce } = useAccessibility();
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);

  const handleQuantityChange = (id: number, newValue: string) => {
    const quantity = parseInt(newValue);
    if (!isNaN(quantity)) {
      updateQuantity(id, quantity);
      const item = cartItems.find(item => item.id === id);
      if (item) {
        announce(`Updated quantity of ${item.name} to ${quantity}`, "polite");
      }
    }
  };

  const handleCheckout = () => {
    if (isAuthenticated) {
      navigate('/checkout');
      announce("Proceeding to checkout", "polite");
    } else {
      setLoginPromptOpen(true);
      announce("Please sign in to proceed to checkout", "polite");
    }
  };

  const handleClearCart = () => {
    setConfirmClearOpen(true);
    announce("Confirm clearing your cart", "polite");
  };

  const handleConfirmClear = () => {
    clearCart();
    setConfirmClearOpen(false);
    announce("Cart cleared", "polite");
  };

  const handleCancelClear = () => {
    setConfirmClearOpen(false);
    announce("Clear cart cancelled", "polite");
  };

  const handleLoginRedirect = () => {
    setLoginPromptOpen(false);
    navigate('/login', { state: { redirectTo: '/checkout' } });
    announce("Redirecting to sign in page", "polite");
  };

  const handleSignupRedirect = () => {
    setLoginPromptOpen(false);
    navigate('/signup', { state: { redirectTo: '/checkout' } });
    announce("Redirecting to sign up page", "polite");
  };

  const handleRemoveItem = (id: number) => {
    const item = cartItems.find(item => item.id === id);
    removeFromCart(id);
    if (item) {
      announce(`Removed ${item.name} from your cart`, "polite");
    }
  };

  if (cartItems.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Your Cart
        </Typography>
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom role="status">
            Your cart is empty
          </Typography>
          <Button
            component={RouterLink}
            to="/products"
            variant="contained"
            color="primary"
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 2 }}
            aria-label="Continue shopping"
          >
            Continue Shopping
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Your Cart
      </Typography>

      <Paper elevation={2} sx={{ p: 4 }}>
        <Grid container spacing={3}>
          {/* Cart items */}
          <Grid item xs={12} role="region" aria-label="Cart items">
            {cartItems.map((item) => (
              <Card 
                key={item.id} 
                sx={{ 
                  display: 'flex', 
                  mb: 2, 
                  p: 2,
                  borderLeft: '4px solid #FFEB3B' 
                }}
              >
                <CardMedia
                  component="img"
                  sx={{ width: 80, height: 80, objectFit: 'contain' }}
                  image={item.thumbnail}
                  alt={item.name}
                  loading="lazy"
                  style={{
                    maxWidth: '80px',
                    maxHeight: '80px',
                  }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, flexGrow: 1 }}>
                  <Typography variant="h6" component="div">
                    {item.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Price: ${item.price}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Typography variant="body2" mr={1} id={`quantity-label-${item.id}`}>
                      Quantity:
                    </Typography>
                    <TextField
                      type="number"
                      size="small"
                      inputProps={{ 
                        min: 1, 
                        style: { textAlign: 'center' },
                        'aria-labelledby': `quantity-label-${item.id}`
                      }}
                      value={item.quantity}
                      onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                      sx={{ width: 60 }}
                      aria-label={`Quantity for ${item.name}`}
                    />
                    <IconButton 
                      color="error" 
                      onClick={() => handleRemoveItem(item.id)}
                      size="small"
                      sx={{ ml: 'auto' }}
                      aria-label={`Remove ${item.name} from cart`}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pl: 2 }}>
                  <Typography variant="h6" aria-label={`Total for ${item.name}: $${(parseFloat(item.price) * item.quantity).toFixed(2)}`}>
                    ${(parseFloat(item.price) * item.quantity).toFixed(2)}
                  </Typography>
                </Box>
              </Card>
            ))}
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 2 }}>
              <Typography variant="h6" id="cart-total-label">Total:</Typography>
              <Typography variant="h6" aria-labelledby="cart-total-label">${totalPrice}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                component={RouterLink}
                to="/products"
                aria-label="Continue shopping"
              >
                Continue Shopping
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCheckout}
                disabled={cartItems.length === 0}
                startIcon={<ShoppingCartCheckoutIcon />}
                sx={{ ml: 'auto' }}
                aria-label="Proceed to checkout"
              >
                Checkout
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleClearCart}
                disabled={cartItems.length === 0}
                aria-label="Clear cart"
              >
                Clear Cart
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Clear Cart Confirmation Dialog */}
      <Dialog 
        open={confirmClearOpen} 
        onClose={handleCancelClear}
        aria-labelledby="clear-cart-title"
        aria-describedby="clear-cart-description"
      >
        <DialogTitle id="clear-cart-title">Clear Cart</DialogTitle>
        <DialogContent id="clear-cart-description">
          <Typography>Are you sure you want to clear your entire cart?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClear} aria-label="Cancel clearing cart">Cancel</Button>
          <Button onClick={handleConfirmClear} color="error" variant="contained" aria-label="Confirm clear cart">Clear</Button>
        </DialogActions>
      </Dialog>

      {/* Login Prompt Dialog */}
      <Dialog 
        open={loginPromptOpen} 
        onClose={() => setLoginPromptOpen(false)}
        aria-labelledby="login-prompt-title"
        aria-describedby="login-prompt-description"
      >
        <DialogTitle id="login-prompt-title">Sign In Required</DialogTitle>
        <DialogContent id="login-prompt-description">
          <Typography>You need to be logged in to proceed to checkout.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoginPromptOpen(false)} aria-label="Cancel checkout">Cancel</Button>
          <Button 
            onClick={handleSignupRedirect} 
            color="primary" 
            variant="outlined"
            sx={{ fontWeight: 'medium' }}
            aria-label="Sign up for an account"
          >
            Sign Up
          </Button>
          <Button onClick={handleLoginRedirect} color="primary" variant="contained" aria-label="Sign in to your account">
            Sign In
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Cart; 